import React, { useCallback, useEffect, useMemo, useState } from 'react'
import debounce from 'lodash.debounce'
import humps from 'humps'

import { logErrorAsync } from '_lib/utility'
import { getDataExport } from '_services/data-explorer'

import { useEventFeedsContext } from '../../../context'
import { useIsSnapshot } from '../../../manage-component'
import { getPredictorsDataPreview } from '_services/data-explorer'
import { AGGREGATION_OPTIONS_NOT_SUPPORTED_BY_DATA_PREVIEW } from '../../../query-panel/utils'

export const formatTableData = (tableData) => {
  if (!tableData) {
    return
  }

  return tableData.map((row) =>
    // Transform { categories: [{ text: 'some value' }]} into 'some value'
    Object.entries(row).reduce((acc, [key, cellData]) => {
      if (Array.isArray(cellData)) {
        const normalizedData = cellData
          .map((data) => data.text || data.content || `${data}`)
          .sort()
          .join(', ')
        return { ...acc, [key]: normalizedData }
      }
      return { ...acc, [key]: cellData }
    }, {})
  )
}

export const formatTableColumns = (tableColumns) => {
  let columns = []
  if (tableColumns?.length < 1) {
    return columns
  }

  for (const index in tableColumns) {
    if ({}.hasOwnProperty.call(tableColumns, index)) {
      let name = tableColumns[index]
      let column = { name, key: name, index, width: 'unset' }

      column.render = (item) => <span className="data-preview__table_cell">{item[name]}</span>
      column.render.displayName = 'TableCell'
      columns.push(column)
    }
  }
  return columns
}

export default function useDataPreview(search, predictors) {
  const { data, id } = useEventFeedsContext()
  const isSnapshot = useIsSnapshot()

  const [loading, setLoading] = useState(true)
  const [tableColumns, setTableColumns] = useState([])
  const [tableData, setTableData] = useState([])
  const [objectData, setObjectData] = useState([])
  const [totalEvents, setTotalEvents] = useState([])

  const filteredColumns = useMemo(
    () => (search ? tableColumns.filter((column) => column.name.includes(search)) : tableColumns),
    [search, tableColumns]
  )

  const getData = useCallback(
    debounce(async (feedData, isSnapshotFeed) => {
      const getDataFunc = predictors ? getPredictorsDataPreview : getDataExport

      const requestConfig = {
        attributes: feedData.attributes,
        filters: feedData.filters,
        aggregations: feedData.aggregations,
        custom_attributes: feedData.customAttributes,
      }
      setLoading(true)
      try {
        const response = isSnapshotFeed
          ? humps.decamelizeKeys(feedData.snapshot.dataExport)
          : await getDataFunc(requestConfig)
        setObjectData(response.rows)
        setTableData(formatTableData(response?.rows))
        setTableColumns(formatTableColumns(response?.columns))
        setTotalEvents(response.total_events)
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
      setLoading(false)
    }, 1000),
    [predictors]
  )

  useEffect(() => {
    if (
      (data._id || id === 'discovery') &&
      data.attributes?.length > 0 &&
      AGGREGATION_OPTIONS_NOT_SUPPORTED_BY_DATA_PREVIEW.every(
        (attribute) => !data.attributes.includes(attribute)
      )
    ) {
      getData(data, isSnapshot)
    } else {
      setLoading(false)
      setObjectData([])
      setTableData([])
      setTableColumns([])
      setTotalEvents([])
    }
  }, [data.filters, data.attributes, data.customAttributes, data._id, isSnapshot])

  return { loading, filteredColumns, tableColumns, tableData, objectData, totalEvents }
}
